import { confirmLibraryUpload, getPresignedUploadUrl, uploadToS3 } from "@/api";
import { PDF_SPREADSHEET_ACCEPT } from "@/config/constants";
import { trackDocumentUploaded } from "@/lib/analytics";
import {
    AttachmentType,
    IAttachmentOption,
} from "@/api/attachments/attachments.types";
import { IModalAction, Modal } from "@/components/shared/Modal";
import {
    Box,
    CircularProgress,
    IconButton,
    LinearProgress,
    Stack,
    Typography,
    useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { AlertCircle, CheckCircle, Upload, X } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import {
    countsUploadedFileAgainstBudget,
    DirectDocumentBudgetNotice,
    DirectDocumentBudgetRetryBanner,
    useOptionalDirectDocumentBudget,
} from "@/components/shared/direct-document-budget";

interface ChatUploadModalProps {
    open: boolean;
    onClose: () => void;
    onConfirm: (attachments: IAttachmentOption[]) => void;
    /** Files dropped on the page before the modal opened. Seeded once per
     *  open through the same onDrop path as the inner dropzone, so uploads
     *  start immediately. Callers pre-validate type and size. */
    initialFiles?: File[];
}

interface FileUploadItem {
    id: string;
    file: File;
    objectKey: string | null;
    progress: number;
    status: "uploading" | "ready" | "failed";
    abortController: AbortController | null;
}

export const ChatUploadModal = ({
    open,
    onClose,
    onConfirm,
    initialFiles,
}: ChatUploadModalProps) => {
    const theme = useTheme();
    const queryClient = useQueryClient();
    const [files, setFiles] = useState<FileUploadItem[]>([]);
    const [isConfirming, setIsConfirming] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const nextIdRef = useRef(0);
    const initialFilesConsumedRef = useRef(false);

    const isAnyUploading = files.some((f) => f.status === "uploading");
    const readyCount = files.filter((f) => f.status === "ready").length;

    // Uploaded PDFs are structurally always new: the document does not exist
    // until `confirmLibraryUpload` runs, so there is no id to dedup against and
    // the count-based path is the only correct one here. Spreadsheets are
    // exempt, so a batch of 2 PDFs and 3 spreadsheets is judged on the 2.
    const budget = useOptionalDirectDocumentBudget();
    const countedReadyCount = files.filter(
        (f) => f.status === "ready" && countsUploadedFileAgainstBudget(f.file)
    ).length;
    const evaluation = budget?.evaluateNewCount(countedReadyCount) ?? null;
    const budgetBlocks = evaluation != null && !evaluation.admissible;
    const budgetUnavailable =
        budget != null &&
        (budget.status === "pending" ||
            budget.status === "error" ||
            budget.limit == null) &&
        countedReadyCount > 0;

    const uploadFileToS3 = useCallback(
        async (item: FileUploadItem, abortController: AbortController) => {
            try {
                const presigned = await getPresignedUploadUrl(item.file.name);
                await uploadToS3(
                    presigned.upload_url,
                    item.file,
                    (progress) => {
                        setFiles((prev) =>
                            prev.map((f) =>
                                f.id === item.id ? { ...f, progress } : f
                            )
                        );
                    },
                    abortController.signal
                );
                setFiles((prev) =>
                    prev.map((f) =>
                        f.id === item.id
                            ? {
                                  ...f,
                                  objectKey: presigned.object_key,
                                  status: "ready" as const,
                              }
                            : f
                    )
                );
            } catch (err) {
                // Don't mark as failed if the upload was intentionally aborted
                if (
                    err instanceof Error &&
                    (err.name === "CanceledError" || err.name === "AbortError")
                )
                    return;
                setFiles((prev) =>
                    prev.map((f) =>
                        f.id === item.id
                            ? { ...f, status: "failed" as const }
                            : f
                    )
                );
            }
        },
        []
    );

    const onDrop = useCallback(
        (acceptedFiles: File[]) => {
            const newItems: FileUploadItem[] = acceptedFiles.map((file) => {
                const abortController = new AbortController();
                return {
                    id: String(nextIdRef.current++),
                    file,
                    objectKey: null,
                    progress: 0,
                    status: "uploading" as const,
                    abortController,
                };
            });
            setFiles((prev) => [...prev, ...newItems]);
            newItems.forEach((item) =>
                uploadFileToS3(item, item.abortController!)
            );
        },
        [uploadFileToS3]
    );

    // A ref, not state, so StrictMode's double effect cannot seed twice.
    useEffect(() => {
        if (!open) {
            initialFilesConsumedRef.current = false;
            return;
        }
        if (initialFiles?.length && !initialFilesConsumedRef.current) {
            initialFilesConsumedRef.current = true;
            onDrop(initialFiles);
        }
    }, [open, initialFiles, onDrop]);

    const handleRemoveFile = useCallback((id: string) => {
        setFiles((prev) => {
            const file = prev.find((f) => f.id === id);
            if (file?.abortController) file.abortController.abort();
            return prev.filter((f) => f.id !== id);
        });
    }, []);

    const handleClose = () => {
        if (isConfirming) return;
        // Abort any in-progress uploads before clearing state
        files.forEach((f) => {
            if (f.status === "uploading" && f.abortController) {
                f.abortController.abort();
            }
        });
        setFiles([]);
        setError(null);
        onClose();
    };

    const handleAttach = async () => {
        const readyFiles = files.filter((f) => f.status === "ready");
        if (readyFiles.length === 0) return;
        // Re-check here, not only on the button: `onDrop` starts the S3 upload
        // the moment a file lands, so there is no earlier point to preempt at,
        // and the budget can move between render and click. The server seams
        // remain the enforcement boundary; this only avoids a round trip.
        if (budgetBlocks || budgetUnavailable) return;

        setIsConfirming(true);
        setError(null);

        try {
            const attachments: IAttachmentOption[] = await Promise.all(
                readyFiles.map(async (item) => {
                    const doc = await confirmLibraryUpload({
                        object_key: item.objectKey!,
                        document_name: item.file.name,
                    });
                    trackDocumentUploaded({
                        library_document_id: doc.id,
                        folder_id: null,
                        is_knowledge_base: false,
                        source: "chat_upload",
                    });
                    return {
                        attachment_id: doc.id,
                        attachment_type: AttachmentType.Document,
                        attachment_name: doc.document_name,
                        attachment_subtitle: "",
                        // Usually null for fresh uploads (client auto-assign
                        // runs async post-ingestion); null means no
                        // cross-client warning, by design.
                        client_id: doc.client?.id ?? null,
                        // Carry the uploaded file's type so the composer pill
                        // shows spreadsheet (green) vs PDF (red) immediately,
                        // instead of defaulting to PDF until a reload.
                        file_type: doc.file_type ?? null,
                    };
                })
            );

            queryClient.invalidateQueries({
                queryKey: ["frontend-library-list"],
            });

            setFiles([]);
            onConfirm(attachments);
        } catch {
            setError("Failed to create document. Please try again.");
        } finally {
            setIsConfirming(false);
        }
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        // Chat attachments accept spreadsheets too (mirrors the library
        // upload surfaces); only the scope/comparison document pickers stay
        // PDF-only.
        accept: PDF_SPREADSHEET_ACCEPT,
        maxSize: 200 * 1024 * 1024,
        disabled: isConfirming,
    });

    const actions: IModalAction[] = [
        {
            label: isConfirming
                ? "Saving..."
                : isAnyUploading
                  ? "Uploading..."
                  : `Attach${readyCount > 1 ? ` (${readyCount})` : ""}`,
            onClick: handleAttach,
            disabled:
                isConfirming ||
                isAnyUploading ||
                readyCount === 0 ||
                budgetBlocks ||
                budgetUnavailable,
        },
    ];

    return (
        <Modal
            open={open}
            onClose={handleClose}
            title="Upload Documents"
            subtitle="Documents will be saved to your library"
            maxWidth="sm"
            showCancel
            isLoading={isConfirming}
            error={error}
            actions={actions}
            alerts={[
                {
                    severity: "info",
                    message:
                        "Documents take a few minutes to process, so Polly may not be able to read their contents right away.",
                },
            ]}
        >
            <Stack spacing={2}>
                {budget != null && (
                    <Stack spacing={1}>
                        {budget.status === "error" && (
                            <DirectDocumentBudgetRetryBanner
                                onRetry={budget.retry}
                                isRetrying={budget.isRetrying}
                            />
                        )}
                        {budgetBlocks && evaluation != null && (
                            <DirectDocumentBudgetNotice
                                variant="would-exceed"
                                limit={evaluation.limit}
                                newCount={evaluation.proposedCount}
                            />
                        )}
                    </Stack>
                )}
                <Box
                    {...getRootProps()}
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        p: 3,
                        minHeight: 120,
                        borderRadius: 2,
                        border: "2px dashed",
                        borderColor: isDragActive ? "primary.main" : "divider",
                        bgcolor: isDragActive
                            ? alpha(theme.palette.primary.main, 0.04)
                            : "transparent",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        "&:hover": {
                            borderColor: "primary.main",
                            bgcolor: alpha(theme.palette.primary.main, 0.02),
                        },
                    }}
                >
                    <input {...getInputProps()} />
                    <Upload
                        size={28}
                        style={{
                            color: isDragActive
                                ? theme.palette.primary.main
                                : theme.palette.text.secondary,
                            marginBottom: 8,
                        }}
                    />
                    <Typography
                        variant="body2"
                        fontWeight={500}
                        color={isDragActive ? "primary" : "text.primary"}
                    >
                        {isDragActive
                            ? "Drop files here"
                            : "Drop PDFs, Excel or CSV here or click to browse"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        PDF, XLSX or CSV up to 200MB each
                    </Typography>
                </Box>

                {files.length > 0 && (
                    <Stack spacing={1}>
                        {files.map((item) => (
                            <Box
                                key={item.id}
                                sx={{
                                    p: 1.5,
                                    bgcolor: "grey.50",
                                    borderRadius: 1,
                                    border: "1px solid",
                                    borderColor:
                                        item.status === "failed"
                                            ? "error.main"
                                            : "divider",
                                }}
                            >
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={1.5}
                                >
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            color:
                                                item.status === "ready"
                                                    ? "success.main"
                                                    : item.status === "failed"
                                                      ? "error.main"
                                                      : "primary.main",
                                        }}
                                    >
                                        {item.status === "uploading" ? (
                                            <CircularProgress size={18} />
                                        ) : item.status === "failed" ? (
                                            <AlertCircle size={18} />
                                        ) : (
                                            <CheckCircle size={18} />
                                        )}
                                    </Box>
                                    <Stack flex={1} minWidth={0}>
                                        <Typography
                                            variant="body2"
                                            fontWeight={500}
                                            noWrap
                                        >
                                            {item.file.name}
                                        </Typography>
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            spacing={0.5}
                                        >
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                            >
                                                {(
                                                    item.file.size /
                                                    (1024 * 1024)
                                                ).toFixed(2)}{" "}
                                                MB
                                            </Typography>
                                            {item.status === "uploading" && (
                                                <Typography
                                                    variant="caption"
                                                    color="primary"
                                                    fontWeight={500}
                                                >
                                                    &middot; {item.progress}%
                                                </Typography>
                                            )}
                                            {item.status === "ready" && (
                                                <Typography
                                                    variant="caption"
                                                    color="success.main"
                                                    fontWeight={500}
                                                >
                                                    &middot; Ready
                                                </Typography>
                                            )}
                                            {item.status === "failed" && (
                                                <Typography
                                                    variant="caption"
                                                    color="error"
                                                    fontWeight={500}
                                                >
                                                    &middot; Failed
                                                </Typography>
                                            )}
                                        </Stack>
                                        {item.status === "uploading" && (
                                            <LinearProgress
                                                variant="determinate"
                                                value={item.progress}
                                                sx={{
                                                    mt: 0.5,
                                                    height: 3,
                                                    borderRadius: 1,
                                                }}
                                            />
                                        )}
                                    </Stack>
                                    <IconButton
                                        size="small"
                                        onClick={() =>
                                            handleRemoveFile(item.id)
                                        }
                                        disabled={isConfirming}
                                        sx={{ color: "text.secondary" }}
                                    >
                                        <X size={16} />
                                    </IconButton>
                                </Stack>
                            </Box>
                        ))}
                    </Stack>
                )}
            </Stack>
        </Modal>
    );
};
